import { Icons } from '@/components/icon';
import { Button } from '@/components/ui/design-system/button/button';
import { IconButton } from '@/components/ui/design-system/icon-button/icon-button';
import { useTranslation } from '@/hooks/use-translation';
import type { components as remoteComponents } from '@/services/api-allo/api-allo-remote';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAlloRemote } from '@/services/api-allo/client-remote';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

type Contact = components['schemas']['Contact'];

type Props = {
  selectedContacts: Contact[];
  onClear: () => void;
};

const buildQueueNumber = (
  contact: Contact,
): remoteComponents['schemas']['QueueNumberInput'] | null => {
  const number = contact.numbers?.[0];
  if (!number) {
    return null;
  }
  const company =
    typeof contact.company === 'string'
      ? contact.company
      : ((contact.company as { name?: string | null } | null | undefined)?.name ?? undefined);
  return {
    number,
    name: contact.name ?? undefined,
    lastName: contact.last_name ?? undefined,
    company: company ?? undefined,
    website: contact.website ?? undefined,
    jobTitle: contact.job_title ?? undefined,
    emails: contact.emails?.length ? contact.emails : undefined,
  };
};

export const PushToPowerDialerBar = ({ selectedContacts, onClear }: Props) => {
  const { t } = useTranslation('desktop_ui');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();

  const appendNumbers = $apiAlloRemote.useMutation('post', '/v1/dialing-queues/current/numbers');

  // Held through the exit animation so the count doesn't snap to 0 mid-fade.
  const [displayCount, setDisplayCount] = useState(selectedContacts.length);
  useEffect(() => {
    if (selectedContacts.length > 0) {
      setDisplayCount(selectedContacts.length);
    }
  }, [selectedContacts.length]);

  const isVisible = selectedContacts.length > 0;

  const performAppend = async (numbers: remoteComponents['schemas']['QueueNumberInput'][]) => {
    try {
      const response = await appendNumbers.mutateAsync({ body: { numbers } });
      const skipped = (response as { data?: { skipped?: unknown[] } } | undefined)?.data?.skipped;
      const skippedCount = Array.isArray(skipped) ? skipped.length : 0;
      const addedCount = numbers.length - skippedCount;

      if (addedCount > 0) {
        toast.success(t('components.contacts.pushToPowerDialer.addedToast', { count: addedCount }));
      }
      if (skippedCount > 0) {
        toast.warning(
          t('components.contacts.pushToPowerDialer.skippedToast', { count: skippedCount }),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: $apiAlloRemote.queryOptions('get', '/v1/dialing-queues/current', {}).queryKey,
      });

      onClear();
      navigate({ to: '/power-dialer' });
    } catch {
      toast.error(t('components.contacts.pushToPowerDialer.error'));
    }
  };

  // Must stay sync on the early-return path: returning a Promise traps the
  // DS Button in `useAsyncClick`'s pending state.
  const handleSend = () => {
    const numbers = selectedContacts
      .map(buildQueueNumber)
      .filter((n): n is remoteComponents['schemas']['QueueNumberInput'] => n !== null);

    if (numbers.length === 0) {
      toast.error(t('components.contacts.pushToPowerDialer.noPhoneNumbers'));
      return;
    }

    return performAppend(numbers);
  };

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-6 z-sticky flex justify-center px-4">
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: reduceMotion ? 0 : 0.3, ease: 'easeOut' }}
            className="pointer-events-auto flex items-center gap-2 overflow-clip rounded-2xl border border-primary-10 bg-white py-2 pr-1 pl-4 shadow-md"
          >
            <div className="flex items-center gap-1">
              <span className="flex flex-col items-center justify-center text-body-14-bold text-primary-100">
                {displayCount}
              </span>
              <span className="text-body-12 text-primary-70">
                {t('components.contacts.pushToPowerDialer.selected', { count: displayCount })}
              </span>
            </div>

            <Button
              variant="primary"
              size="sm"
              onClick={handleSend}
              isLoading={appendNumbers.isPending}
            >
              {t('components.contacts.pushToPowerDialer.send')}
            </Button>

            <IconButton
              variant="ghost"
              size="sm"
              icon={Icons.Cancel}
              label={t('components.contacts.pushToPowerDialer.dismiss')}
              onClick={onClear}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
